'use client'

import { Button, Card, Cell, Divider, InputText, Spacer, Text } from '@vinted/web-ui'
import { camelCase, omit } from 'lodash'
import { useEffect, useMemo } from 'react'
import { Controller, FormProvider, useForm } from 'react-hook-form'

import { useIntl } from 'react-intl'

import {
  DocumentGroup,
  PaymentsIdentityConfigurationModel,
  PaymentsIdentityFileModel,
} from '@marketplace-web/pay-identity/payments-identity-data'
import { ErrorItem } from '@marketplace-web/core-api/api-client-util'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import { useSession } from '@marketplace-web/shared/session-data'
import { InputBirthdate } from '@marketplace-web/common-components/input-birthdate-ui'
import { SeparatedList } from '@marketplace-web/common-components/separated-list-ui'
import {
  renderValidation,
  useFormValidationMessage,
} from '@marketplace-web/vendor-abstractions/react-hook-form-feature'
import { Heading, ErrorBanner } from '@marketplace-web/business-accounts/business-accounts-ui'
import { updateBusinessAccountRepresentativeDetails } from '@marketplace-web/business-accounts/business-accounts-data'
import { KybFormFields, KybFormModel } from '@marketplace-web/pay-identity/pay-identity-kyc-data'
import { useBreakpoint } from '@marketplace-web/breakpoints/breakpoints-feature'

import { KybFormData } from './useSubmitKybFormRequest'
import { isFieldRequired } from './utils'
import IdentityDocument from '../KybIdentityDocument'
import SupportingDocuments from '../KybSupportingDocuments/SupportingDocuments'
import Ubo from '../BusinessDetails/Ubo'
import { KybFieldName } from '../../constants'
import { KYC_HELP_URL } from '../../constants/routes'
import { isValueInObject } from '../../utils'

type Props = {
  kybFormConfiguration: PaymentsIdentityConfigurationModel | null
  onSubmit: (data: KybFormData) => void
  apiErrors: Array<ErrorItem> | null
  loading: boolean
  showGeneralError: boolean
  kybFormData: KybFormData
  onChangeKYBFormData: (data: Partial<KybFormData>) => void
}

const KybFormContent = ({
  kybFormConfiguration,
  onSubmit,
  onChangeKYBFormData,
  kybFormData,
  apiErrors,
  loading,
  showGeneralError,
}: Props) => {
  const translate = useTranslate('business.kyb')
  const generalErrorTranslate = useTranslate('business.registration.errors.general')
  const inputBirthdateTranslate = useTranslate()
  const intl = useIntl()
  const breakpoints = useBreakpoint()

  const supportingDocumentsConfiguration = useMemo(
    () =>
      kybFormConfiguration?.documentFieldConfigurations?.filter(
        ({ group, options }) => group === DocumentGroup.SupportingDocuments && options.length === 1,
      ),
    [kybFormConfiguration?.documentFieldConfigurations],
  )

  const isSupportingDocumentsRequired = useMemo(() => {
    const supportingDocumentTypes = supportingDocumentsConfiguration?.map(
      ({ options }) => options[0]?.type,
    )

    return kybFormConfiguration?.requiredFields?.some(field =>
      supportingDocumentTypes?.includes(field),
    )
  }, [kybFormConfiguration?.requiredFields, supportingDocumentsConfiguration])

  const identityDocumentConfiguration = useMemo(
    () =>
      kybFormConfiguration?.documentFieldConfigurations?.filter(
        ({ group }) => group === DocumentGroup.IdentityDocuments,
      )?.[0],
    [kybFormConfiguration?.documentFieldConfigurations],
  )

  const userId = useSession().user?.id

  const getDefaultValues = (): Partial<KybFormModel> => {
    const supportingDocuments = supportingDocumentsConfiguration
      ?.filter(({ options }) =>
        isFieldRequired(
          kybFormConfiguration?.requiredFields || [],
          options[0]?.type as KybFieldName,
        ),
      )
      .map(({ options }) => ({
        type: options[0]?.type || '',
        files:
          options[0]?.files.map(({ identifier }) => ({
            identifier,
            file: null,
            body: null,
            mediaType: null,
          })) || [],
      }))

    return {
      ubos: [],
      supportingDocuments,
    }
  }

  const formMethods = useForm<KybFormModel>({
    defaultValues: getDefaultValues(),
  })

  const isIdentityDocumentRequired = isFieldRequired(
    kybFormConfiguration?.requiredFields || [],
    KybFieldName.IdentityDocument,
  )
  const isUboRequired = isFieldRequired(
    kybFormConfiguration?.requiredFields || [],
    KybFieldName.Ubos,
  )

  const {
    setError,
    handleSubmit,
    control,
    formState: { errors },
    getValues,
  } = formMethods

  const validationMessage = useFormValidationMessage(errors, 'business.kyb')

  const handleFormSubmit = handleSubmit(
    async ({ identityDocument, supportingDocuments, ...values }) => {
      const expiryDate = getValues(KybFormFields.IdentityDocumentExpiryDate)
      const documents: Array<PaymentsIdentityFileModel> = []

      if (!expiryDate || !userId) return

      if (identityDocument) documents.push(identityDocument)
      if (supportingDocuments) documents.push(...supportingDocuments)

      const submitValues = {
        ...kybFormData,
        ...values,
        documents,
      }

      onChangeKYBFormData(submitValues)

      const result = await updateBusinessAccountRepresentativeDetails({
        identityDocumentExpirationDate: expiryDate,
      })
      if ('errors' in result) {
        setError(KybFormFields.IdentityDocumentExpiryDate, {
          type: 'manual',
          message: result.errors[0]!.value,
        })

        return
      }

      onSubmit(submitValues)
    },
  )

  useEffect(() => {
    if (!apiErrors) return

    apiErrors.forEach(({ field, value }) => {
      const fieldName = camelCase(field)

      if (!isValueInObject(fieldName, KybFormFields)) return

      if (fieldName === KybFormFields.IdentityDocument) {
        setError(KybFormFields.IdentityDocument, { type: 'manual', message: value })

        return
      }

      setError(fieldName, { type: 'manual', message: value })
    })
  }, [apiErrors, setError])

  const renderSupportingDocuments = () => {
    if (!isSupportingDocumentsRequired) return null

    return (
      <>
        <Card>
          <Cell
            styling={Cell.Styling.Wide}
            body={
              <Text
                as="h3"
                type={Text.Type.Subtitle}
                text={translate('supporting_documents.title')}
              />
            }
          />
          <SupportingDocuments
            isFormDisabled={false}
            supportingDocumentsConfiguration={supportingDocumentsConfiguration}
          />
        </Card>
        <Spacer size={Spacer.Size.XLarge} />
      </>
    )
  }

  const renderIdentityDocument = () => {
    if (!isIdentityDocumentRequired) return null

    return (
      <Card>
        <SeparatedList separator={<Divider />}>
          <IdentityDocument identityDocumentConfiguration={identityDocumentConfiguration} />
          <Controller
            render={({ field }) => {
              const fieldPropsWithoutRef = omit(field, 'ref')

              return (
                <InputBirthdate
                  {...fieldPropsWithoutRef}
                  isPhone={!!breakpoints.phones}
                  styling={InputText.Styling.Wide}
                  name={KybFormFields.IdentityDocumentExpiryDate}
                  title={translate('identity_document_expiry_date.title')}
                  dayPlaceholder={inputBirthdateTranslate('birthdate.day')}
                  monthPlaceholder={inputBirthdateTranslate('birthdate.month')}
                  yearPlaceholder={inputBirthdateTranslate('birthdate.year')}
                  isoLocale={intl.locale}
                  validation={renderValidation(
                    validationMessage(KybFormFields.IdentityDocumentExpiryDate),
                  )}
                />
              )
            }}
            name={KybFormFields.IdentityDocumentExpiryDate}
            control={control}
            rules={{
              required: true,
            }}
          />
        </SeparatedList>
      </Card>
    )
  }

  const renderUbos = () => {
    if (!isUboRequired) return null

    return (
      <Ubo
        isFormDisabled={false}
        isLegalRepresentativeUbo={kybFormData.isLegalRepresentativeUbo}
        setIsLegalRepresentativeUbo={value => {
          onChangeKYBFormData({ isLegalRepresentativeUbo: value })
        }}
      />
    )
  }

  return (
    <FormProvider {...formMethods}>
      <form onSubmit={handleFormSubmit} noValidate className="container">
        <Spacer size={Spacer.Size.X2Large} />
        <Heading
          styling={Cell.Styling.Tight}
          title={translate('title')}
          subtitle={translate('subtitle')}
        />
        {showGeneralError && (
          <>
            <Spacer size={Spacer.Size.X2Large} />
            <ErrorBanner
              title={generalErrorTranslate('title')}
              body={generalErrorTranslate('body')}
            />
          </>
        )}

        <Spacer size={Spacer.Size.X2Large} />
        {renderSupportingDocuments()}
        {renderIdentityDocument()}
        {renderUbos()}
        <Spacer size={Spacer.Size.X2Large} />
        <div className="u-flexbox u-flex-wrap-reverse c-input__value--justified u-align-items-center">
          <Text
            as="span"
            text={translate('mangopay_link', {
              'mangopay-link': chunks => (
                <a key="mangopay-link" href={KYC_HELP_URL} target="_blank" rel="noreferrer">
                  {chunks}
                </a>
              ),
            })}
          />
          <Button
            type={Button.Type.Submit}
            styling={Button.Styling.Filled}
            text={translate('buttons.submit')}
            testId="kyb-form--submit-button"
            isLoading={loading}
            inline
            onClick={(event: React.MouseEvent<HTMLAnchorElement | HTMLButtonElement>) => {
              if (loading) {
                event.preventDefault()
              }
            }}
          />
        </div>
        <Spacer size={Spacer.Size.X6Large} />
      </form>
    </FormProvider>
  )
}

export default KybFormContent
